import {
  DATA_PROVIDER_DISPLAY_NAMES,
  EHR_NETWORK_PROVIDERS,
} from '@ctw/shared/api/fhir/mappings/data-provider-names';
import type {
  PatientHistoryJobProviderStatus,
  PatientHistoryJobResponseJobData,
  PatientHistoryJobStatus,
} from '@ctw/shared/api/patient-history/patient-history-types';
import { format } from 'date-fns';
import type { PatientModel } from './patient';

export const completedProviderStatus: ReadonlyArray<PatientHistoryJobProviderStatus> = [
  'done',
  'skipped',
];

export const failedProviderStatus: ReadonlyArray<PatientHistoryJobProviderStatus> = [
  'error',
  'done_with_info',
];

export type PatientHistoryJobDerivedStatus =
  | 'queued'
  | 'inProgress'
  | 'complete'
  | 'failed'
  | 'partiallyComplete' // Partially complete is a combination of in-progress and complete
  | 'completeWithErrors'; // Complete with errors is a combination of complete and failed

export type PatientHistoryJobDerivedProvider = {
  isEhrNetwork: boolean;
  service: string;
  serviceDisplayName: string;
  status: PatientHistoryJobProviderStatus;
  derivedStatus: PatientHistoryJobDerivedStatus;
};

export class PatientHistoryRequestModel {
  public kind = 'PatientHistory' as const;

  public patient: PatientModel;
  public historyInfo: PatientHistoryJobResponseJobData;

  public constructor(patient: PatientModel, historyInfo: PatientHistoryJobResponseJobData) {
    this.patient = patient;
    this.historyInfo = historyInfo;
  }

  public get key() {
    return this.historyInfo.id;
  }

  public get rawStatus() {
    return this.historyInfo.attributes.jobStatus;
  }

  public get isScheduled() {
    return (['queued', 'scheduled'] satisfies Array<PatientHistoryJobStatus>).includes(
      this.historyInfo.attributes.jobStatus as never,
    );
  }

  public get isInProgress() {
    return (
      this.historyInfo.attributes.jobStatus === ('in_progress' satisfies PatientHistoryJobStatus)
    );
  }

  public get isCompleted() {
    return (['done', 'error'] satisfies Array<PatientHistoryJobStatus>).includes(
      this.historyInfo.attributes.jobStatus as never,
    );
  }

  public get providers() {
    return this.historyInfo.attributes.providers;
  }

  public get rawTargetDate() {
    // This is necessary because targetDate was not introduced until May 2023 and it was not backfilled
    return new Date(
      this.historyInfo.attributes.targetDate ??
        Number(this.historyInfo.attributes.lastUpdatedAt) * 1000,
    );
  }

  public get targetDate() {
    return format(this.rawTargetDate, 'M/d/yyyy');
  }

  public get createdAt() {
    return format(
      new Date(Number(this.historyInfo.attributes.createdAt) * 1000),
      "M/d/yy 'at' h:mm a",
    );
  }

  public get lastUpdatedAt() {
    return format(
      new Date(Number(this.historyInfo.attributes.lastUpdatedAt) * 1000),
      "M/d/yy 'at' h:mm a",
    );
  }

  public get derivedStatus(): PatientHistoryJobDerivedStatus {
    return getDerivedProviderStatus(this.providers.map((provider) => provider.status));
  }

  public get derivedProviders(): Array<PatientHistoryJobDerivedProvider> {
    return this.providers.map((provider) => {
      return {
        isEhrNetwork: EHR_NETWORK_PROVIDERS.includes(provider.service),
        service: provider.service,
        serviceDisplayName: DATA_PROVIDER_DISPLAY_NAMES[provider.service] ?? provider.service,
        status: provider.status,
        derivedStatus: getDerivedProviderStatus([provider.status]),
      };
    });
  }

  public get ehrNetworkStatus(): PatientHistoryJobDerivedStatus | undefined {
    const ehrNetworkProviders = this.derivedProviders.filter((provider) => provider.isEhrNetwork);
    if (ehrNetworkProviders.length === 0) {
      return undefined;
    }

    return getDerivedProviderStatus(ehrNetworkProviders.map((provider) => provider.status));
  }
}

export const getDerivedProviderStatus = (
  statuses: Array<PatientHistoryJobProviderStatus>,
): PatientHistoryJobDerivedStatus => {
  if (statuses.every((status) => completedProviderStatus.includes(status))) {
    return 'complete';
  }

  if (statuses.every((status) => failedProviderStatus.includes(status))) {
    return 'failed';
  }

  if (
    statuses.every((status) =>
      [...completedProviderStatus, ...failedProviderStatus].includes(status),
    )
  ) {
    return 'completeWithErrors';
  }

  if (statuses.some((status) => completedProviderStatus.includes(status))) {
    return 'partiallyComplete';
  }

  if (statuses.some((status) => status === 'unset')) {
    return 'queued';
  }

  return 'inProgress';
};
